import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ArtworkCard from "@/components/ArtworkCard";
import StructuredData from "@/components/StructuredData";
import SEOHead from "@/components/SEOHead";
import FloatingActions from "@/components/FloatingActions";
import { Search, Filter, Grid3X3, List } from "lucide-react";
import { useArtworks } from "@/hooks/useArtworks";

const Gallery = () => {
  const { artworks, loading } = useArtworks();
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [formatFilter, setFormatFilter] = useState("");
  const [canvasFilter, setCanvasFilter] = useState("");
  const [mediumFilter, setMediumFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortType, setSortType] = useState<"default" | "price-desc" | "price-asc">("default");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  // Объединяем все работы и добавляем поля order если их нет
  const allArtworks = [...artworks.featured, ...artworks.gallery].map((artwork, index) => ({
    ...artwork,
    order: (artwork as any).order ?? index + 1
  }));

  // Фильтрация работ
  const filteredArtworks = allArtworks.filter(artwork => {
    const matchesSearch = artwork.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         artwork.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         artwork.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = !categoryFilter || artwork.category === categoryFilter;
    const matchesFormat = !formatFilter || artwork.format === formatFilter;
    const matchesCanvas = !canvasFilter || artwork.canvas === canvasFilter;
    const matchesMedium = !mediumFilter || artwork.medium === mediumFilter;
    const matchesStatus = !statusFilter || artwork.status === statusFilter;
    
    return matchesSearch && matchesCategory && matchesFormat && matchesCanvas && matchesMedium && matchesStatus;
  });

  // Сортировка в зависимости от выбранного типа
  const sortedArtworks = filteredArtworks.sort((a, b) => {
    switch (sortType) {
      case "price-desc":
        return Number(b.price) - Number(a.price);
      case "price-asc":
        return Number(a.price) - Number(b.price);
      case "default":
      default:
        // Сортировка по полю order (позиция из админки), если нет - по индексу
        const orderA = a.order ?? allArtworks.findIndex(item => item.id === a.id) + 1;
        const orderB = b.order ?? allArtworks.findIndex(item => item.id === b.id) + 1;
        return orderA - orderB;
    }
  });

  const resetFilters = () => {
    setSearchTerm("");
    setCategoryFilter("");
    setFormatFilter("");
    setCanvasFilter("");
    setMediumFilter("");
    setStatusFilter("");
    setSortType("default");
  };

  // Получаем уникальные категории
  const categories = [...new Set(allArtworks.filter(art => art.category).map(art => art.category))];


  return (
    <>
      <SEOHead
        title="Галерея картин художника из Омска - Валерия Вагнер | W-Wagner.art"
        description="Галерея авторских картин маслом и акрилом Валерии Вагнер из Омска. Более 150 работ: пейзажи, абстракции, натюрморты. Купить картины в Омске с доставкой по России. Цены от 4000₽."
        keywords={['галерея картин Омск', 'купить картину Омск', 'картины маслом Омск', 'пейзажи Омск', 'абстракция Омск', 'натюрморты Омск', 'авторские картины', 'Валерия Вагнер картины']}
        canonicalUrl="https://w-wagner.art/raboty"
      />
      <StructuredData type="gallery" />
      
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-card via-background to-card/50">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,hsl(var(--primary)/0.1),transparent_50%)]" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
            <div className="text-center space-y-8">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary font-medium">
                  <Search className="w-4 h-4" />
                  Портфолио художника
                </div>
                <h1 className="text-6xl md:text-8xl font-bold">
                  <span className="bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                    Галерея
                  </span>
                </h1>
                <p className="text-2xl md:text-3xl text-muted-foreground font-light max-w-4xl mx-auto">
                  Коллекция авторских картин Валерии Вагнер. Каждая работа создана с особой любовью и вниманием к деталям.
                </p>
              </div>
              
              <div className="flex flex-wrap justify-center gap-3">
                {["Картины маслом", "Пейзажи", "Абстракции"].map((tag) => (
                  <Badge 
                    key={tag}
                    variant="outline" 
                    className="text-base px-6 py-3 border-primary/30 hover:border-primary/50 transition-colors"
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </section>

        <div className="py-8 md:py-12 gallery-container">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">

            {/* Filters */}
            <Card className="mb-8">
              <CardContent className="p-4 md:p-6">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-4 mb-6">
                  <div className="flex items-center gap-2">
                    <Filter className="w-5 h-5 text-muted-foreground" />
                    <h3 className="text-lg font-medium">Фильтры</h3>
                  </div>
                  
                  <div className="flex items-center gap-2 ml-auto">
                    <Button variant="outline" size="sm" onClick={resetFilters}>
                      Сбросить
                    </Button>
                    <div className="flex rounded-md border">
                      <Button
                        variant={viewMode === "grid" ? "secondary" : "ghost"}
                        size="sm"
                        onClick={() => setViewMode("grid")}
                        className="rounded-r-none"
                      >
                        <Grid3X3 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant={viewMode === "list" ? "secondary" : "ghost"}
                        size="sm"
                        onClick={() => setViewMode("list")}
                        className="rounded-l-none"
                      >
                        <List className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
                  {/* Поиск */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Поиск..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  {/* Сортировка */}
                  <Select value={sortType} onValueChange={(value) => setSortType(value as "default" | "price-desc" | "price-asc")}>
                    <SelectTrigger>
                      <SelectValue placeholder="Сортировка" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Обычная</SelectItem>
                      <SelectItem value="price-desc">По цене ↓</SelectItem>
                      <SelectItem value="price-asc">По цене ↑</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Фильтр по категории */}
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Категория" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(category => (
                        <SelectItem key={category} value={category} className="capitalize">
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Фильтр по формату */}
                  <Select value={formatFilter} onValueChange={setFormatFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Формат" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="горизонтальная">Горизонтальная</SelectItem>
                      <SelectItem value="вертикальная">Вертикальная</SelectItem>
                      <SelectItem value="квадратная">Квадратная</SelectItem>
                      <SelectItem value="круглая">Круглая</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Фильтр по холсту */}
                  <Select value={canvasFilter} onValueChange={setCanvasFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Холст" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="картон">Картон</SelectItem>
                      <SelectItem value="подрамник">Подрамник</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Фильтр по краскам */}
                  <Select value={mediumFilter} onValueChange={setMediumFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Материал" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="масло">Масло</SelectItem>
                      <SelectItem value="акрил">Акрил</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Фильтр по статусу */}
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Статус" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="в наличии">В наличии</SelectItem>
                      <SelectItem value="продано">Продано</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Результаты поиска */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                  <Badge variant="secondary">
                    Найдено: {filteredArtworks.length} из {allArtworks.length}
                  </Badge>
                  <p className="text-sm text-muted-foreground">
                    {sortType === "default" && "Сортировка обычная"}
                    {sortType === "price-desc" && "Сортировка по цене (убыванию)"}
                    {sortType === "price-asc" && "Сортировка по цене (возрастанию)"}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Gallery Grid */}
            <div className="min-h-[600px] w-full" style={{ contain: "layout" }}>
              {loading ? (
                <div className="text-center py-16 flex items-center justify-center min-h-[400px]">
                  <div className="max-w-md mx-auto">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Загрузка картин...</p>
                  </div>
                </div>
              ) : filteredArtworks.length > 0 ? (
                <div className={viewMode === "grid" ? "artwork-grid" : "space-y-6"}>
                  {sortedArtworks.map((artwork) => (
                    <ArtworkCard
                      key={artwork.id}
                      {...artwork}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 flex items-center justify-center min-h-[400px]">
                  <div className="max-w-md mx-auto">
                    <div className="text-6xl mb-4">🎨</div>
                    <h3 className="text-xl font-semibold mb-2">Ничего не найдено</h3>
                    <p className="text-muted-foreground mb-6">
                      Попробуйте изменить параметры поиска или сбросить фильтры
                    </p>
                    <Button variant="outline" onClick={resetFilters}>
                      Сбросить фильтры
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <FloatingActions currentPage="gallery" />
      </div>
    </>
  );
};

export default Gallery;